/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar,
  Grid,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Pages as PagesIcon,
  SystemUpdateAlt as UpdateIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { format, parseISO, subDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useAuth } from '../contexts/AuthContext';

export default function AccessLogCard({ onOpen }) {
  const { isAdmin } = useAuth();
  
  // Não renderiza se não for admin
  if (!isAdmin || !isAdmin()) {
    return null;
  }

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        borderRadius: 4,
        boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.16)',
        },
        width: 150,
        height: 150,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          p: 0,
          pt: '10px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexGrow: 1,
          width: '100%',
          gap: 0,
        }}
      >
        <Avatar
          sx={{
            width: 60,
            height: 60,
            mb: '10px',
            bgcolor: '#9c27b0',
            alignSelf: 'center',
          }}
        >
          <HistoryIcon sx={{ fontSize: 32, color: 'white' }} />
        </Avatar>
        <Typography
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            lineHeight: 1.1,
            overflow: 'hidden',
            height: { xs: 36, sm: 36 },
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
          }}
        >
          LOG DE ACESSO
        </Typography>
        <Box sx={{ height: { xs: 20, sm: 20 } }} />
      </CardContent>
    </Card>
  );
}

export function AccessLogModal({ open, onClose }) {
  const { token, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [deployLogs, setDeployLogs] = useState([]);
  const [loadingDeploys, setLoadingDeploys] = useState(false);
  const [userSessions, setUserSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [dataInicio, setDataInicio] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dataFim, setDataFim] = useState(format(new Date(), 'yyyy-MM-dd'));

  const API = '/api';

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', `${dataInicio}T00:00:00`);
      if (dataFim) params.append('data_fim', `${dataFim}T23:59:59`);
      params.append('limit', '200');

      const res = await fetch(`${API}/access-log/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      } else if (res.status === 403) {
        setError('Acesso restrito a administradores');
      } else {
        setError('Erro ao carregar logs');
      }
    } catch (e) {
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [token, API, dataInicio, dataFim]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/access-log/stats/?dias=7`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Erro ao carregar estatísticas:', e);
    }
  }, [token, API]);

  const fetchDeployLogs = useCallback(async () => {
    if (!token) return;
    setLoadingDeploys(true);
    try {
      const res = await fetch(`${API}/deploy-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDeployLogs(data);
      }
    } catch (e) {
      console.error('Erro ao carregar deploy logs:', e);
    } finally {
      setLoadingDeploys(false);
    }
  }, [token, API]);

  const fetchUserSessions = useCallback(async () => {
    if (!token) return;
    setLoadingSessions(true);
    try {
      const res = await fetch(`${API}/access-log/user-sessions?dias=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUserSessions(data);
      }
    } catch (e) {
      console.error('Erro ao carregar sessões de usuários:', e);
    } finally {
      setLoadingSessions(false);
    }
  }, [token, API]);

  useEffect(() => {
    if (open) {
      fetchLogs();
      fetchStats();
      fetchDeployLogs();
      fetchUserSessions();
    }
  }, [open, fetchLogs, fetchStats, fetchDeployLogs, fetchUserSessions]);

  const handleExportDailyLog = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const res = await fetch(`${API}/access-log/daily/?data=${today}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `access_log_${today}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (e) {
      setError('Erro ao exportar log');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getAcaoColor = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'LOGIN': return 'success';
      case 'LOGOUT': return 'default';
      case 'CREATE': return 'primary';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'error';
      default: return 'info';
    }
  };

  if (!isAdmin || !isAdmin()) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <span>Log de Acesso do Sistema</span>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Exportar log do dia">
            <IconButton onClick={handleExportDailyLog} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton onClick={() => { fetchLogs(); fetchStats(); fetchDeployLogs(); fetchUserSessions(); }} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isMobile ? (
          <TextField
            select
            fullWidth
            size="small"
            value={tabIndex}
            onChange={(e) => setTabIndex(Number(e.target.value))}
            sx={{ mb: 2 }}
          >
            <MenuItem value={0}><AccessTimeIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />Logs de Acesso</MenuItem>
            <MenuItem value={1}><PagesIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />Estatísticas</MenuItem>
            <MenuItem value={2}><UpdateIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />Atualizações</MenuItem>
            <MenuItem value={3}><PeopleIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />Últimos Acessos</MenuItem>
          </TextField>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {[
              { label: 'Logs de Acesso',  icon: <AccessTimeIcon sx={{ fontSize: 16 }} />, color: '#1565c0', bg: '#e3f2fd', active: '#1565c0' },
              { label: 'Estatísticas',    icon: <PagesIcon      sx={{ fontSize: 16 }} />, color: '#2e7d32', bg: '#e8f5e9', active: '#2e7d32' },
              { label: 'Atualizações',    icon: <UpdateIcon     sx={{ fontSize: 16 }} />, color: '#e65100', bg: '#fff3e0', active: '#e65100' },
              { label: 'Últimos Acessos', icon: <PeopleIcon     sx={{ fontSize: 16 }} />, color: '#6a1b9a', bg: '#f3e5f5', active: '#6a1b9a' },
            ].map((tab, i) => (
              <Button
                key={i}
                onClick={() => setTabIndex(i)}
                startIcon={tab.icon}
                size="small"
                variant={tabIndex === i ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  textTransform: 'none',
                  px: 1.5,
                  ...(tabIndex === i
                    ? { bgcolor: tab.active, color: '#fff', borderColor: tab.active, '&:hover': { bgcolor: tab.active } }
                    : { color: tab.color, borderColor: tab.color, bgcolor: tab.bg, '&:hover': { bgcolor: tab.bg, opacity: 0.85 } }),
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {tabIndex === 0 && (
          <>
            {/* Filtros */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Data Início"
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="Data Fim"
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Button variant="contained" onClick={fetchLogs} disabled={loading}>
                Buscar
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : isMobile ? (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {logs.map((log) => (
                  <Card key={log.id} sx={{ mb: 1.5, borderRadius: 2 }}>
                    <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          {log.usuario_nome}
                        </Typography>
                        <Chip label={log.acao || 'VIEW'} color={getAcaoColor(log.acao)} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">{formatDateTime(log.data_hora)}</Typography>
                      <Typography variant="body2">Página: {log.pagina}</Typography>
                      <Typography variant="body2">IP: {log.ip_address || '-'}</Typography>
                      {log.detalhes && <Typography variant="body2" color="text.secondary">Detalhes: {log.detalhes}</Typography>}
                    </CardContent>
                  </Card>
                ))}
                {logs.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Nenhum registro encontrado</Typography>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data/Hora</TableCell>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Página</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>Ação</TableCell>
                      <TableCell>Detalhes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(log.data_hora)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            {log.usuario_nome}
                          </Box>
                        </TableCell>
                        <TableCell>{log.pagina}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {log.ip_address || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.acao || 'VIEW'}
                            color={getAcaoColor(log.acao)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.detalhes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Nenhum registro encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Exibindo {logs.length} registros
            </Typography>
          </>
        )}

        {tabIndex === 1 && stats && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                Estatísticas dos últimos {stats.periodo_dias} dias - Total: {stats.total_acessos} acessos
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  📊 Páginas mais acessadas
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Página</TableCell>
                      <TableCell align="right">Acessos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.por_pagina?.slice(0, 10).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.pagina}</TableCell>
                        <TableCell align="right">{item.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  👥 Usuários mais ativos
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell align="right">Acessos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.por_usuario?.slice(0, 10).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.usuario}</TableCell>
                        <TableCell align="right">{item.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  🕐 Últimos acessos
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Página</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>Ação</TableCell>
                      <TableCell>Data/Hora</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.ultimos_acessos?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.usuario}</TableCell>
                        <TableCell>{item.pagina}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.ip_address || '-'}</TableCell>
                        <TableCell>
                          <Chip label={item.acao || 'VIEW'} size="small" color={getAcaoColor(item.acao)} />
                        </TableCell>
                        <TableCell>{formatDateTime(item.data_hora)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        )}
        {tabIndex === 2 && (
          <>
            {loadingDeploys ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : isMobile ? (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {deployLogs.map((dl) => (
                  <Card key={dl.id} sx={{ mb: 1.5, borderRadius: 2 }}>
                    <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {dl.versao || 'Deploy'}
                        </Typography>
                        <Chip
                          label={dl.tipo || 'deploy'}
                          color={dl.tipo === 'hotfix' ? 'error' : dl.tipo === 'rollback' ? 'warning' : 'success'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">{formatDateTime(dl.deployed_at)}</Typography>
                      {dl.descricao && <Typography variant="body2">{dl.descricao}</Typography>}
                      {dl.alteracoes && dl.alteracoes.length > 0 && (
                        <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5, fontSize: '0.8rem' }}>
                          {dl.alteracoes.map((a, i) => (<li key={i}>{a}</li>))}
                        </Box>
                      )}
                      {dl.autor && <Typography variant="caption" color="text.secondary">Autor: {dl.autor}</Typography>}
                      {dl.commit_hash && <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>Commit: {dl.commit_hash}</Typography>}
                    </CardContent>
                  </Card>
                ))}
                {deployLogs.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Nenhuma atualização registrada</Typography>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Versão</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell>Alterações</TableCell>
                      <TableCell>Autor</TableCell>
                      <TableCell>Commit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deployLogs.map((dl) => (
                      <TableRow key={dl.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(dl.deployed_at)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={dl.tipo || 'deploy'}
                            color={
                              dl.tipo === 'hotfix' ? 'error' :
                              dl.tipo === 'rollback' ? 'warning' : 'success'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{dl.versao || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>{dl.descricao || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          {dl.alteracoes && dl.alteracoes.length > 0 ? (
                            <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.8rem' }}>
                              {dl.alteracoes.map((a, i) => (
                                <li key={i}>{a}</li>
                              ))}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{dl.autor || '-'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {dl.commit_hash || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {deployLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Nenhuma atualização registrada
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Exibindo {deployLogs.length} atualizações
            </Typography>
          </>
        )}

        {tabIndex === 3 && (
          <>
            {loadingSessions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : isMobile ? (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {userSessions.map((s) => (
                  <Card key={s.usuario_id} sx={{ mb: 1.5, borderRadius: 2 }}>
                    <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          {s.nome} ({s.username})
                        </Typography>
                        <Chip label={s.nivel_acesso} size="small" color="default" />
                      </Box>
                      <Typography variant="body2">Último acesso: {s.ultimo_acesso ? formatDateTime(s.ultimo_acesso) : 'Nunca'}</Typography>
                      <Typography variant="body2">IP: {s.ultimo_ip || '-'}</Typography>
                      <Typography variant="body2">Total (30 dias): {s.total_acessos}</Typography>
                      {s.ultimos_logins?.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Últimos logins:</Typography>
                          {s.ultimos_logins.slice(0, 3).map((l, i) => (
                            <Typography key={i} variant="caption" display="block" sx={{ pl: 1 }}>
                              {formatDateTime(l.data_hora)} - {l.ip_address || '-'}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {userSessions.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Nenhum registro encontrado</Typography>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Nome</TableCell>
                      <TableCell>Nível</TableCell>
                      <TableCell>Último Acesso</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell align="right">Acessos (30d)</TableCell>
                      <TableCell>Últimos Logins</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userSessions.map((s) => (
                      <TableRow key={s.usuario_id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{s.username}</TableCell>
                        <TableCell>{s.nome}</TableCell>
                        <TableCell>
                          <Chip label={s.nivel_acesso} size="small" />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {s.ultimo_acesso ? formatDateTime(s.ultimo_acesso) : 'Nunca'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.ultimo_ip || '-'}</TableCell>
                        <TableCell align="right">{s.total_acessos}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {s.ultimos_logins?.slice(0, 3).map((l, i) => (
                            <Box key={i}>{formatDateTime(l.data_hora)}</Box>
                          ))}
                          {(!s.ultimos_logins || s.ultimos_logins.length === 0) && '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {userSessions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">Nenhum registro encontrado</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Exibindo {userSessions.length} usuários (últimos 30 dias)
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
